/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import { FeatureState } from '@msdyn365-commerce/retail-proxy';
import { Cart, CartLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import {
    createGetFeatureStateInput,
    CartConfigurationInput,
    getCartConfiguration,
    getCustomer,
    GetCustomerInput,
    getFeatureState,
    getSimpleProducts,
    ProductInput
} from '.';
import { ArrayExtensions } from './utilities';

/**
 * Input class for activeCartWithProducts data action.
 */
export class ActiveCartProductsInput implements IActionInput {
    public getCacheKey = () => 'ActiveCartProducts';

    public getCacheObjectType = () => 'ActiveCartProducts';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (inputData: ICreateActionContext) => {
    return new ActiveCartProductsInput();
};

/**
 * Calls the Retail API and returns a cart object based on the passed GetCartInput.
 * @param input
 * @param ctx
 */
export async function getActiveCartProductsAction(input: ActiveCartProductsInput, ctx: IActionContext): Promise<SimpleProduct[]> {
    // If no cart ID is provided in input, we need to create a cart object
    if (!input) {
        throw new Error('[getActiveCartWithProducts]No valid Input was provided, failing');
    }

    const cartState = await getCartState(ctx);
    const cart = cartState.cart;

    const isQuantityLimitsFeatureIsOn: boolean = await isOrderQuantityLimitsFeatureEnabled(ctx);

    if (isQuantityLimitsFeatureIsOn) {
        return getActiveCartProductsActionWhenQuantityLimitsFeatureIsOn(cart, ctx);
    }

    // If there are cart lines, make call to get products
    if (!cartState.hasInvoiceLine && cart && cart.CartLines && cart.CartLines.length > 0) {
        ctx.trace('Getting cart product information...');
        return getSimpleProducts(
            <ProductInput[]>cart.CartLines.map(cartLine => {
                if (cartLine.ProductId) {
                    return new ProductInput(
                        cartLine.ProductId,
                        ctx.requestContext.apiSettings,
                        undefined,
                        undefined,
                        ctx.requestContext,
                        cartLine.CatalogId
                    );
                }
                return undefined;
            }).filter(Boolean),
            ctx
        )
            .then((products: SimpleProduct[]) => {
                if (products) {
                    return products;
                }
                return [];
            })
            .catch((error: Error) => {
                ctx.trace(error.toString());
                ctx.telemetry.error(error.message);
                ctx.telemetry.debug('[getActiveCartWithProducts]Unable to hydrate cart with product information');
                throw new Error('[getActiveCartWithProducts]Unable to hydrate cart with product information');
            });
    }

    ctx.trace('[getActiveCartWithProducts]No Products Found in cart');
    return <SimpleProduct[]>[];
}

export const getActiveCartProductsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-products-in-active-cart',
    action: <IAction<SimpleProduct[]>>getActiveCartProductsAction,
    input: createInput
});
export default getActiveCartProductsActionDataAction;

async function getActiveCartProductsActionWhenQuantityLimitsFeatureIsOn(cart: Cart, ctx: IActionContext): Promise<SimpleProduct[]> {
    const productIdsByWarehouseId: Map<string, CartLine[]> = new Map();
    let resultProducts: SimpleProduct[] = [];
    cart.CartLines?.forEach(cartLine =>
        productIdsByWarehouseId.has(cartLine.WarehouseId!)
            ? productIdsByWarehouseId.get(cartLine.WarehouseId!)?.push(cartLine)
            : productIdsByWarehouseId.set(cartLine.WarehouseId!, [cartLine])
    );
    return Promise.all(
        [...productIdsByWarehouseId].map(([entryWarehouseId, entryProductIds]) => {
            return getSimpleProducts(
                <ProductInput[]>entryProductIds
                    .map(cartLine => {
                        if (cartLine.ProductId) {
                            return new ProductInput(
                                cartLine.ProductId,
                                ctx.requestContext.apiSettings,
                                undefined,
                                entryWarehouseId,
                                ctx.requestContext,
                                cartLine.CatalogId
                            );
                        }
                        return undefined;
                    })
                    .filter(Boolean),
                ctx
            ).then((products: SimpleProduct[]) => {
                if (products) {
                    resultProducts = products.reduce((accum, product) => {
                        if (product) {
                            accum.push(product);
                        }
                        return accum;
                    }, resultProducts);
                }
            });
        })
    ).then(() => resultProducts);
}

async function isOrderQuantityLimitsFeatureEnabled(ctx: IActionContext): Promise<boolean> {
    const featureStates = await getFeatureState(createGetFeatureStateInput(ctx), ctx);
    let isQuantityLimitsFeatureEnabledInHq = false;
    if (ArrayExtensions.hasElements(featureStates)) {
        isQuantityLimitsFeatureEnabledInHq =
            featureStates.find(
                (featureState: FeatureState) => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature'
            )?.IsEnabled || false;
    }

    if (!isQuantityLimitsFeatureEnabledInHq) {
        return false;
    }

    const useSiteBuilderSettings = featureStates?.find(
        featureState => featureState.Name === 'RevertToSiteBuilderOrderQuantityLimitsSettings'
    )?.IsEnabled;

    if (useSiteBuilderSettings) {
        const defaultOrderQuantityLimitsFeatureConfig = ctx.requestContext.app?.platform?.enableDefaultOrderQuantityLimits;
        if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
            return Promise.resolve(false);
        }
        if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
            return Promise.resolve(true);
        }

        return getCustomer(new GetCustomerInput(ctx.requestContext.apiSettings), ctx)
            .then(customerInfo => {
                return (
                    !!customerInfo &&
                    ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                        (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
                );
            })
            .catch((error: Error) => {
                ctx.telemetry.warning(error.message);
                ctx.telemetry.debug('Unable to get customer info');
                return false;
            });
    } else {
        return getCartConfiguration(new CartConfigurationInput(), ctx)
            .then(cartConfiguration => {
                return !(cartConfiguration?.IgnoreOrderSettings ?? false);
            })
            .catch((error: Error) => {
                ctx.telemetry.warning(error.message);
                ctx.telemetry.debug('Unable to get cart configuration');
                return false;
            });
    }
}
